import React, { useEffect, useState } from "react";
import { Formik } from "formik";
import { useI18next } from "../../../../plugins/gatsby-plugin-ap-i18next/src/useI18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "@reach/router";
import { navigate } from "gatsby";
import * as Yup from "yup";

import { container, draw, fieldset, form, start } from "./generator.module.scss";
import {
    EGeneratorFilterKeys,
    IGeneratorFormValues,
    IGeneratorProductsSetPayload,
    TGeneratorFilters,
} from "../../../models/generator.model";
import { TStatus } from "../../../models/status.model";
import {
    selectGeneratorProducts,
    selectGeneratorProductsStatus,
} from "../../../redux/generator-products/generator-products.selectors";
import {
    clearGeneratorFiltersAction,
    getGeneratorFiltersAction,
} from "../../../redux/generator-filters/generator-filters.actions";
import {
    selectGeneratorFilters,
    selectGeneratorFiltersStatus,
} from "../../../redux/generator-filters/generator-filters.selectors";
import {
    clearGeneratorProductsAction,
    getGeneratorProductsAction,
} from "../../../redux/generator-products/generator-products.actions";
import { createSingleStylizationAction } from "../../../redux/single-stylization/single-stylization.actions";
import { selectSingleStylizationStatus } from "../../../redux/single-stylization/single-stylization.selectors";

import FormikForm from "../../../components/hoc/formik-form";
import GeneratorStartFields from "../organisms/generator-start-fields";
import GeneratorDrawFields from "../organisms/generator-draw-fields";
import Loader from "../../../components/atoms/loader";
import useUser from "../../../apps/auth/hooks/use-user";
import { useModalContext } from "../../../contexts/modal.context";
import { EModal } from "../../../models/modal.model";

interface IGeneratorLocationState {
    isSurveySuccessRedirect?: boolean;
}

const Generator: React.FC = () => {
    const { t } = useI18next();
    const location = useLocation();
    const locationState = location.state as IGeneratorLocationState | undefined;
    const isSurveySuccessRedirect = locationState?.isSurveySuccessRedirect;
    const initialName = isSurveySuccessRedirect ? t("generator.stylization.first") : "";
    const dispatch = useDispatch();
    const filters: TGeneratorFilters = useSelector(selectGeneratorFilters);
    const filtersStatus: TStatus = useSelector(selectGeneratorFiltersStatus);
    const { sessionId, sessionGuid, items }: IGeneratorProductsSetPayload = useSelector(selectGeneratorProducts);
    const productsStatus: TStatus = useSelector(selectGeneratorProductsStatus);
    const stylizationStatus: TStatus = useSelector(selectSingleStylizationStatus);
    const hasGeneratedProducts = sessionId !== null || !!sessionGuid || items.length > 0;
    const [hasStarted, setHasStarted] = useState(false);
    const [initialFormValues, setInitialFormValues] = useState(
        getFormValues({ ...initialValues, name: initialName }, filters)
    );

    const isLoading = getLoadingStatus(filtersStatus, productsStatus, stylizationStatus, sessionId);
    const [counterClick, setCounterClick] = useState(1);
    const { isLoggedIn } = useUser();
    const { addModal } = useModalContext();

    const showDemoLimitModal = (content: string) => {
        addModal({
            key: EModal.CONFIRMATION,
            props: {
                title: "Zaloguj się, aby działać dalej",
                content,
                confirmText: "Zaloguj się",
                cancelText: "Zostań tutaj",
                onConfirm: () => navigate("/auth/"),
            },
        });
    };

    const handleSubmit = (values: IGeneratorFormValues) => {
        const nextValues = isLoggedIn ? values : getDemoFormValues(values, filters);
        if (!isLoggedIn) {
            const limitResult = registerDemoGenerationAttempt();
            if (!limitResult.allowed) {
                showDemoLimitModal(limitResult.message);
                return;
            }
        }

        setHasStarted(true);
        dispatch(getGeneratorProductsAction(nextValues));
        setCounterClick((prev) => prev + 1);
        //window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleSave = (values: IGeneratorFormValues) => () => {
        dispatch(
            createSingleStylizationAction({
                name: values.name,
                guid: sessionGuid,
                productIds: items.map((product) => product.productId),
                occasionId: values[EGeneratorFilterKeys.occasion],
            })
        );
    };

    useEffect(() => {
        dispatch(getGeneratorFiltersAction());
        return () => {
            dispatch(clearGeneratorFiltersAction());
            dispatch(clearGeneratorProductsAction());
        };
    }, [dispatch]);

    useEffect(() => {
        // console.log("[generator]filters", filters);
        const currentSeason = getSeasonFromDate();
        // console.log("currentSeason", currentSeason);
        const seasonLabels = {
            wiosna: "Wiosna",
            lato: "Lato",
            jesien: "Jesień",
            zima: "Zima"
        };
        //filters.seasons = ["wiosna", "lato", "jesien", "zima"].map((value) => ({ value: value, label: value, applied: value === currentSeason }));

        const filtersSeasons = Object.entries(seasonLabels).map(([value, label]) => ({
            value,
            label,
            applied: value === currentSeason
        }));
        filters.seasons = filtersSeasons;
        // console.log("[generator]filters 2", filters);
        const nextValues = getFormValues({ ...initialValues, name: initialName }, filters);
        setInitialFormValues(isLoggedIn ? nextValues : getDemoFormValues(nextValues, filters));
    }, [filters, isLoggedIn]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [productsStatus]);


    const [loadingStylizationsListTexts] = useState([
        "Czaruje dla Ciebie...",
        "Już za chwilkę wyczaruje..."
    ]);

    return (
        <div className={container}>
            {isLoading && <Loader position="fixed" texts={loadingStylizationsListTexts} />}
            <Formik
                onSubmit={handleSubmit}
                initialValues={initialFormValues}
                enableReinitialize={true}
                validationSchema={isLoggedIn ? getValidationSchema(t) : null}
            >
                {(formik) => (
                    <FormikForm
                        formik={formik}
                        successMessage=""
                        className={form}
                        fieldsetClassName={fieldset}
                        loaderType="none"
                    >
                        {hasStarted && hasGeneratedProducts ? (
                            <GeneratorDrawFields
                                className={draw}
                                filters={filters}
                                products={items}
                                onSpaceKeydown={formik.submitForm}
                                onSave={handleSave(formik.values)}
                                counterClick={counterClick}
                            />
                        ) : (
                            <GeneratorStartFields
                                className={start}
                                filters={filters}
                                onOccasionSelect={handleSubmit}
                            />
                        )}
                    </FormikForm>
                )}
            </Formik>
        </div>
    );
};

function getSeasonFromDate(date: Date = new Date()): string {
    const month = date.getMonth() + 1; // getMonth() zwraca 0–11
    //   console.log("Miesiąc:", month);

    if (month >= 3 && month <= 5) return "wiosna";  // marzec–maj
    if (month >= 6 && month <= 8) return "lato";    // czerwiec–sierpień
    if (month >= 9 && month <= 11) return "jesien"; // wrzesień–listopad
    return "zima";                                  // grudzień–luty
}

const initialValues: IGeneratorFormValues = {
    name: "",
    blockedProducts: [],
    [EGeneratorFilterKeys.colors]: [],
    [EGeneratorFilterKeys.patterns]: [],
    [EGeneratorFilterKeys.occasion]: "",
    [EGeneratorFilterKeys.style]: "",
    [EGeneratorFilterKeys.priceTop]: "",
    [EGeneratorFilterKeys.priceBottom]: "",
    [EGeneratorFilterKeys.priceOuterwear]: "",
    [EGeneratorFilterKeys.priceOverall]: "",
    [EGeneratorFilterKeys.seasons]: "",
};

const DEMO_GENERATION_LIMIT_STORAGE_KEY = "fashionfairy-demo-generator-attempts";
const DEMO_GENERATION_MINUTE_LIMIT = 5;
const DEMO_GENERATION_HOUR_LIMIT = 30;
const ONE_MINUTE_MS = 60 * 1000;
const ONE_HOUR_MS = 60 * ONE_MINUTE_MS;

function registerDemoGenerationAttempt(): { allowed: true } | { allowed: false; message: string } {
    if (typeof window === "undefined") {
        return { allowed: true };
    }

    const now = Date.now();
    const rawAttempts = window.localStorage.getItem(DEMO_GENERATION_LIMIT_STORAGE_KEY);
    let attempts: number[] = [];

    if (rawAttempts) {
        try {
            attempts = JSON.parse(rawAttempts).filter((timestamp: unknown) => {
                return typeof timestamp === "number" && now - timestamp < ONE_HOUR_MS;
            });
        } catch {
            attempts = [];
        }
    }

    const attemptsInMinute = attempts.filter((timestamp) => now - timestamp < ONE_MINUTE_MS);

    if (attemptsInMinute.length >= DEMO_GENERATION_MINUTE_LIMIT) {
        window.localStorage.setItem(DEMO_GENERATION_LIMIT_STORAGE_KEY, JSON.stringify(attempts));
        return {
            allowed: false,
            message:
                "Osiągnięto limit generowania dla niezalogowanych użytkowników. Pełna funkcjonalność generatora jest dostępna po zalogowaniu.",
        };
    }

    if (attempts.length >= DEMO_GENERATION_HOUR_LIMIT) {
        window.localStorage.setItem(DEMO_GENERATION_LIMIT_STORAGE_KEY, JSON.stringify(attempts));
        return {
            allowed: false,
            message:
                "Osiągnięto limit generowania dla niezalogowanych użytkowników. Zaloguj się, aby korzystać z pełnej funkcjonalności generatora.",
        };
    }

    attempts.push(now);
    window.localStorage.setItem(DEMO_GENERATION_LIMIT_STORAGE_KEY, JSON.stringify(attempts));

    return { allowed: true };
}

function getValidationSchema(t: ReturnType<typeof useI18next>["t"]) {
    
    return Yup.object({
        name: Yup.string().required(t("form.validation.required")),
        [EGeneratorFilterKeys.occasion]: Yup.string().required(t("form.validation.required")),
        [EGeneratorFilterKeys.style]: Yup.string().required(t("form.validation.required")),
        [EGeneratorFilterKeys.priceTop]: Yup.lazy((value) =>
            typeof value === "number"
                ? Yup.number().required(t("form.validation.required"))
                : Yup.string().required(t("form.validation.required"))
        ),
        [EGeneratorFilterKeys.priceBottom]: Yup.lazy((value) =>
            typeof value === "number"
                ? Yup.number().required(t("form.validation.required"))
                : Yup.string().required(t("form.validation.required"))
        ),
        [EGeneratorFilterKeys.priceOuterwear]: Yup.lazy((value) =>
            typeof value === "number"
                ? Yup.number().required(t("form.validation.required"))
                : Yup.string().required(t("form.validation.required"))
        ),
        [EGeneratorFilterKeys.priceOverall]: Yup.lazy((value) =>
            typeof value === "number"
                ? Yup.number().required(t("form.validation.required"))
                : Yup.string().required(t("form.validation.required"))
        ),
    });
}

type TValuesFromFilters = Partial<
    Record<keyof IGeneratorFormValues, string | number | string[] | number[]>
>;

function getFormValues(
    initialValues: IGeneratorFormValues,
    filters: TGeneratorFilters
): IGeneratorFormValues {
    const keys = Object.keys(filters) as Array<keyof TGeneratorFilters>;
    const values = keys.reduce((result: TValuesFromFilters, key) => {
        const appliedOptions = filters[key].filter((option) => option.applied);
        const isArray = Array.isArray(initialValues[key]);
        let keyValue;
        if (isArray) {
            keyValue = appliedOptions
                .map((option) => {
                    return option.value || option.id;
                })
                .filter((value) => !!value) as string[] | number[];
        } else {
            keyValue = appliedOptions[0]?.value || appliedOptions[0]?.id;
        }
        if (keyValue) {
            result[key] = keyValue;
        }
        return result;
    }, {});
    return {
        ...initialValues,
        ...values,
    } as IGeneratorFormValues;
}

function getOptionValue(option: TGeneratorFilters[keyof TGeneratorFilters][number]) {
    return option.id !== undefined ? option.id : option.value;
}

function getDefaultSingleValue(
    filters: TGeneratorFilters,
    key: keyof TGeneratorFilters,
    preferredIndex = 0
) {
    const options = filters[key] || [];
    const appliedOption = options.find((option) => option.applied);
    const fallbackOption = appliedOption || options[preferredIndex] || options[0];

    return fallbackOption ? getOptionValue(fallbackOption) : "";
}

function getDefaultMultiValue(filters: TGeneratorFilters, key: keyof TGeneratorFilters) {
    const options = filters[key] || [];
    const appliedOptions = options.filter((option) => option.applied);
    const selectedOptions = appliedOptions.length ? appliedOptions : options;

    return selectedOptions.map(getOptionValue).filter((value) => !!value) as string[] | number[];
}

function getDemoFormValues(
    values: IGeneratorFormValues,
    filters: TGeneratorFilters
): IGeneratorFormValues {
    return {
        ...values,
        [EGeneratorFilterKeys.colors]:
            values[EGeneratorFilterKeys.colors]?.length
                ? values[EGeneratorFilterKeys.colors]
                : getDefaultMultiValue(filters, EGeneratorFilterKeys.colors),
        [EGeneratorFilterKeys.patterns]:
            values[EGeneratorFilterKeys.patterns]?.length
                ? values[EGeneratorFilterKeys.patterns]
                : getDefaultMultiValue(filters, EGeneratorFilterKeys.patterns),
        [EGeneratorFilterKeys.style]:
            values[EGeneratorFilterKeys.style] ||
            getDefaultSingleValue(filters, EGeneratorFilterKeys.style),
        [EGeneratorFilterKeys.seasons]:
            values[EGeneratorFilterKeys.seasons] ||
            getDefaultSingleValue(filters, EGeneratorFilterKeys.seasons),
        [EGeneratorFilterKeys.priceTop]:
            values[EGeneratorFilterKeys.priceTop] ||
            getDefaultSingleValue(filters, EGeneratorFilterKeys.priceTop, 1),
        [EGeneratorFilterKeys.priceBottom]:
            values[EGeneratorFilterKeys.priceBottom] ||
            getDefaultSingleValue(filters, EGeneratorFilterKeys.priceBottom, 1),
        [EGeneratorFilterKeys.priceOuterwear]:
            values[EGeneratorFilterKeys.priceOuterwear] ||
            getDefaultSingleValue(filters, EGeneratorFilterKeys.priceOuterwear, 1),
        [EGeneratorFilterKeys.priceOverall]:
            values[EGeneratorFilterKeys.priceOverall] ||
            getDefaultSingleValue(filters, EGeneratorFilterKeys.priceOverall, 1),
    };
}

function getLoadingStatus(
    filtersStatus: TStatus,
    productsStatus: TStatus,
    stylizationStatus: TStatus,
    sessionId: number | null
): boolean {
    if (!filtersStatus || filtersStatus === "idle" || filtersStatus === "loading") return true;
    if (productsStatus === "loading") return true;
    return stylizationStatus === "loading";
}

export default Generator;
